// @flow

// WARNING:
// This plugin is used outside the editor by Godzilla in the EditorPreview component:
// https://github.com/zapier/zapier/blob/develop/web/frontend/assets/app/developer-v3/components/EditorPreview/EditorPreview.js#L12.
// It's used there to show what a form would look like in the editor as the user is creating their dev
// app in Godizilla.
// WHEN YOU MAKE CHANGES TO THIS FILE MAKE SURE YOU DO NOT BREAK GODZILLA.
// Depending on the nature of the change you may need to test Godzilla as well as the editor.

import Formatic from 'formatic';
import React from 'react';
import _ from 'lodash';
import createReactClass from 'create-react-class'; // eslint-disable-line no-restricted-syntax
import keycode from 'keycode';
import memoize from 'memoize-weak';
import bemify, { toModifiers } from 'bemify-js';
import { $set, update } from 'qim';

import { PreviewPill } from '@zapier/editor';
import { serviceUtils } from '@zapier/common-utils';

import AddSearch from 'app/beta-editor/components/formatic/AddSearch';
import Choice from 'app/beta-editor/components/formatic/Choice';
import EmptyFieldsMessage from 'app/beta-editor/components/formatic/EmptyFieldsMessage';
import ExpandableFieldHelp from 'app/beta-editor/components/ExpandableFieldHelp';
import Filter from 'app/beta-editor/components/formatic/Filter';
import FilterCategoryLabel from 'app/beta-editor/components/formatic/FilterCategoryLabel';
import FlankedInput from 'app/auth/components/FlankedInput';
import NodeUtils from 'app/beta-editor/utils/NodeUtils';
import ServiceUtils from 'app/service/ServiceUtils';
import FieldUtils, {
  getSelectedChoiceForField,
} from 'app/beta-editor/utils/FieldUtils';
import { Div } from 'app/common/components/AccessibleElement';
import { getReloadChoices } from 'app/beta-editor/utils/Choices';
import { getStepIndex } from 'app/beta-editor/utils/FlowUtils';
import { isPathNode } from 'app/beta-editor/utils/PathUtils';
import {
  cleanObjectOfEmptyValues,
  firstItemIfArray,
  get,
  isListChild,
  superHumanize,
} from 'app/common/CommonUtils';

import type { Field } from 'app/beta-editor/types';

// If we want to react to preview changes,
// we need to pass a specific tagLabel into formatic
// that we want to use instead of the regular 'label'.
// see https://github.com/zapier/formatic/pull/83
const DATA_IS_MISSING = <em>No data</em>;

const CUSTOM_CHOICE_LABEL = 'Use a Custom Value (advanced)';

const addTagValueProperty = memoize(choices => {
  return choices.map(choice => ({
    ...choice,
    tagLabel: choice.sample || DATA_IS_MISSING,
  }));
});

const ConvertSearchField = createReactClass({
  displayName: 'ConvertSearchField',
  mixins: [Formatic.availableMixins.field],

  onConvertSearch(event) {
    this.onStartAction('convert-search', {
      isWrite: event.target.checked,
    });
  },

  render() {
    const { isChecked, description } = this.props.field;

    return (
      <div className="fm-field">
        <label className="inline-checkbox">
          <input
            checked={isChecked}
            onChange={this.onConvertSearch}
            type="checkbox"
          />
          {description}
        </label>
      </div>
    );
  },
});

const highlightTrailing = (text, pattern) =>
  React.createElement.apply(React, [
    'span',
    {},
    ...text.split(pattern).map((part, i) => {
      // Because our pattern has a grouping, the grouping will be included in
      // the split. So odd parts will have the trailing spaces used to split.
      const isTrailingSpace = i % 2 !== 0;
      if (isTrailingSpace) {
        return <span className="cm-trailingspace-read-only">{part}</span>;
      } else {
        return part;
      }
    }),
  ]);

const newLineHighlightPattern = /(\s+\n)/g;
const newLineAndEndHighlightPattern = /(\s+\n|\s+$)/g;

const nodeIdFromTag = (tag = '') => {
  const nodeIdString = tag.substring(0, tag.indexOf('__'));
  if (nodeIdString.length < 1) {
    return null;
  }
  try {
    return parseInt(nodeIdString, 10);
  } catch (e) {
    return null;
  }
};

// if you write in a malformed key, usually you'll include
// the node id, and some key to find the data.
// if the node id is correct, but the data key is not,
// formatic will just humanize the data that's there since it's
// not present in the replaceChoices. It will most likely include
// the node id, followed by a space, followed by some words.
// by checking if the humanized versions are identical, we can assume
// it's probably not real data. If the value happened to be strangely
// exactly the same as the node id + key pattern though, this is inaccurate.
const isProbablyHumanizedBogusData = (value = '', key = '') =>
  superHumanize(value) === superHumanize(key);

const isInvalidPill = (field?: Field) => {
  if (!field) return false;
  return FieldUtils.getInvalidMappings(field).indexOf(field.value) > -1;
};

const ZAP_META_REGEXP = /^zap_meta_/;

const isZapMetaValue = (pillValue = '') => {
  return !_.isEmpty(pillValue.match(ZAP_META_REGEXP));
};

const configurePreviewTag = (currentPillValue, tag, field) => {
  if (isZapMetaValue(tag)) {
    return 'Generated when Zap runs';
  }
  if (
    isProbablyHumanizedBogusData(currentPillValue, tag) ||
    isInvalidPill(field)
  ) {
    return DATA_IS_MISSING;
  }
  return currentPillValue;
};

export default (
  // we should come back to this later, 'any' is too broad but this
  config: *,
  {
    shouldUseDefaults = true,
    isZapTemplate = false,
    shouldUseSimpleZapSetup = false,
  }: {
    shouldUseDefaults?: boolean,
    isZapTemplate?: boolean,
    shouldUseSimpleZapSetup?: boolean,
  } = {}
) => {
  const {
    tokenize: origTokenize,
    fieldTemplateTypeName: origFieldTemplateTypeName,
    fieldHelpText: origFieldHelpText,
    createFieldElement: origCreateFieldElement,
    createElement_PrettyText: origCreateElement_PrettyText,
    createElement_Field: origCreateElement_Field,
    createElement_PrettyTextInput: origCreateElement_PrettyTextInput,
    createElement_PrettySelectValue: origCreateElement_PrettySelectValue,
    fieldPrettyChoices: origFieldPrettyChoices,
    renderComponent_Label: origRenderComponent_Label,
    fieldItemFieldTemplates: origFieldItemFieldTemplates,
    fieldTemplateDefaultValue: origFieldTemplateDefaultValue,
    fieldLabel: origFieldLabel,
    createElement_Label: origCreateElement_Label,
    createElement: origCreateElement,
    fieldIsRequired: origIsFieldRequired,
    actionChoiceLabel: origActionChoiceLabel,
    customChoiceDisplayValue: origCustomChoiceDisplayValue,
  } = config;

  return {
    ...config,

    alias_Bool: 'PrettySelect',

    tokenize(text) {
      const tokens = origTokenize(text);
      return tokens.map((token, i) => {
        if (token.type === 'string') {
          // Only consider the end position if this is the last token. Otherwise,
          // a pill is next, so we shouldn't highlight the trailing space at the
          // end.
          const pattern =
            i === tokens.length - 1
              ? newLineAndEndHighlightPattern
              : newLineHighlightPattern;
          return {
            type: 'string',
            value: highlightTrailing(token.value, pattern),
          };
        }
        return token;
      });
    },

    // Always fallback to PrettyText type if we don't recognize the field type.
    fieldTemplateTypeName(...args) {
      let typeName = origFieldTemplateTypeName(...args);
      if (!config.hasElementFactory(typeName)) {
        typeName = 'PrettyText';
      }
      return typeName;
    },

    fieldHelpText(field) {
      const helpText = firstItemIfArray(origFieldHelpText(field));

      // Wrap help text with <p> tag, to make styles work right with HTML help text.
      // But don't wrap with <p> tag if it's already wrapped.
      if (
        helpText &&
        typeof helpText === 'string' &&
        !helpText.match(/^\s*<p>/i)
      ) {
        return `<p>${helpText}</p>`;
      }

      // Don't wrap empty help text with <p> - it messes things up
      return helpText;
    },

    fieldReplaceChoices(field) {
      if (field.meta.replaceChoices !== null) {
        return addTagValueProperty(field.meta.replaceChoices);
      } else {
        return [];
      }
    },

    actionChoiceLabel(action) {
      if (action === 'enter-custom-value') {
        return !isZapTemplate ? (
          CUSTOM_CHOICE_LABEL
        ) : (
          <span>
            <span className="title">{CUSTOM_CHOICE_LABEL}</span>
            <span className="description">
              This field will be populated with data from the user&rsquo;s
              account when they are setting up their Zap. Select this option if
              you&rsquo;d like to hardcode a value into this field for them.
            </span>
          </span>
        );
      }
      return origActionChoiceLabel(action);
    },

    customChoiceDisplayValue(customChoice, currentValue) {
      return isZapTemplate
        ? CUSTOM_CHOICE_LABEL
        : origCustomChoiceDisplayValue(customChoice, currentValue);
    },

    fieldSelectedReplaceChoices(field) {
      return NodeUtils.tempReplaceChoicesFromNode(field);
    },

    fieldIsLoading(field) {
      let needsMeta;
      if (field.meta.node) {
        needsMeta = get(field.meta.flow, ['needsMeta', field.meta.node.id]);
      }
      if (field.parentKey) {
        return get(needsMeta, [field.parentKey, 'isLoading'], false);
      }
      return get(needsMeta, [field.key, 'isLoading'], false);
    },

    createFieldElement(props) {
      // Do not show 'Set up fields' section for search or write fields, if there are no fields to setup:
      if (props.field.isWriteContainer && props.field.fields.length === 0) {
        return null;
      }

      return origCreateFieldElement(props);
    },

    createElement_PrettyText(props) {
      if (FieldUtils.hasInvalidPrettyPills(props.field)) {
        const newProps = update(['classes', 'warning', $set(true)], props);
        return origCreateElement_PrettyText(newProps);
      }
      return origCreateElement_PrettyText(props);
    },

    createElement_EmptyFieldsMessage(props) {
      return <EmptyFieldsMessage {...props} />;
    },

    createElement_Field(props, children) {
      let newProps;

      if (props.field.isWriteSharedWithSearch) {
        newProps = _.extend({}, props);
        newProps.classes = _.extend(
          { 'read-only-field': true },
          newProps.classes
        );
      }

      return origCreateElement_Field(newProps || props, children);
    },

    createElement_PrettyTextInput(props) {
      return (
        <FlankedInput {...props} hasClipboardButton={true}>
          {origCreateElement_PrettyTextInput(props)}
        </FlankedInput>
      );
    },

    fieldIsRequired(field) {
      return isZapTemplate ? false : origIsFieldRequired(field);
    },

    createElement_RequiredLabel({ field }) {
      if (shouldUseSimpleZapSetup) {
        return null;
      }
      // Skip fields that have children.
      const children = config.fieldChildFieldTemplates(field);
      if (!_.isEmpty(children)) return null;
      const isRequired = config.fieldIsRequired(field);
      const labelText = isZapTemplate
        ? origIsFieldRequired(field)
          ? 'required for user'
          : 'optional for user'
        : isRequired
        ? 'required'
        : 'optional';

      return (
        <span
          className={`required-label ${isRequired ? 'required' : 'optional'}`}
        >
          {' '}
          ({labelText})
        </span>
      );
    },

    renderComponent_Copy(component) {
      const markup = {
        __html: component.props.config.fieldHelpText(component.props.field),
      };
      return (
        <div className="fm-copy">
          <span dangerouslySetInnerHTML={markup} />
        </div>
      );
    },

    createElement_PrettySelectValue(props) {
      const field = FieldUtils.isListItem(props.field)
        ? props.field.parent
        : props.field;

      if (field.searchfill) {
        return (
          <AddSearch
            {...props}
            createSelect={origCreateElement_PrettySelectValue}
          />
        );
      }

      return origCreateElement_PrettySelectValue(props);
    },

    fieldPrettyChoices(field) {
      if (
        FieldUtils.hasGivesPrefill(field) &&
        field.meta.replaceChoices !== null
      ) {
        return field.meta.replaceChoices;
      }

      if (field.parentChoices) {
        return field.parentChoices;
      }
      let needsMeta;
      if (field.meta.node) {
        needsMeta = get(field.meta.flow, ['needsMeta', field.meta.node.id]);
      }
      const dynamicChoices = get(needsMeta, [field.key, 'choices']);
      let fieldWithDynamicChoices;
      if (dynamicChoices) {
        fieldWithDynamicChoices = _.extend({}, field, {
          choices: dynamicChoices,
        });
      }
      let choices = origFieldPrettyChoices(fieldWithDynamicChoices || field);
      // Check `zapierType` because `type` might be a `list` of booleans
      if (FieldUtils.isBoolField(field)) {
        // Use the default boolean choices from formatic, but we need to convert the values from
        // booleans to strings like 'true', 'false', to make pretty-select detect changes properly
        choices = config.fieldBooleanChoices(field);
        choices = choices.map(c => ({ ...c, value: String(c.value) }));
      }

      if (field.excludeMetaChoices === true) {
        return choices;
      }

      if (!field.noSpecialValues) {
        // Only offer to "Load More Choices" when there's a prefill.
        if (field.prefill) {
          if (
            !config.fieldIsLoading(field) &&
            !_.find(choices, { action: 'load-more-choices' })
          ) {
            choices = choices.concat(
              getReloadChoices(field, needsMeta, choices.length)
            );
          }
        }

        if (!isZapTemplate || !_.isEmpty(choices)) {
          choices.push({
            sectionKey: 'other-options',
            label: 'Other Options',
            initialState: 'open',
            shouldShowAtEnd: true,
          });
        }
        if (
          !_.find(choices, { action: 'enter-custom-value' }) &&
          !shouldUseSimpleZapSetup
        ) {
          choices.push({
            action: 'enter-custom-value',
            // temporarily disable label to allow actionChoiceLabel for custom JSX label
            label: !isZapTemplate ? CUSTOM_CHOICE_LABEL : '',
            shouldShowInSearch: true,
          });
        }
        if (!_.find(choices, { action: 'clear-current-choice' })) {
          choices.push({
            value: '',
            action: 'clear-current-choice',
            label: 'Clear Current Choice',
            shouldShowInSearch: true,
          });
        }
      }

      return choices;
    },

    fieldSelectedChoice(field) {
      return getSelectedChoiceForField({ field, shouldUseDefaults });
    },

    renderComponent_Label(component) {
      let logoClass = get(component.props.field.meta, ['node', 'selected_api']);
      // this isn't a field that zapier provided as a need (IE: is a field wrapper or list/dict subfield)
      const zapierProvidedField = !component.props.field.zapierType;
      if (zapierProvidedField || !logoClass) {
        return origRenderComponent_Label(component);
      }
      // remove reliance on _.flow and _.flowform styles for this component
      if (component.props.field.type === 'filter') {
        return (
          <FilterCategoryLabel
            id={component.props.id}
            isRequired={component.props.field.required}
            label={component.props.field.label}
          />
        );
      }
      logoClass = serviceUtils.stripVersion(logoClass);
      const wrapperClass = bemify(
        'label-logo-wrapper',
        toModifiers('simplified-editor')
      );
      return (
        <div className={wrapperClass}>
          {origRenderComponent_Label(component)}
        </div>
      );
    },

    renderComponent_Help(component) {
      return (
        <ExpandableFieldHelp
          helpText={component.props.config.fieldHelpText(component.props.field)}
        />
      );
    },

    fieldItemFieldTemplates(field) {
      if (FieldUtils.isListField(field)) {
        let choices = field.choices;
        if (field.prefill) {
          choices = choices || [];
        }
        if (choices || FieldUtils.isBoolField(field)) {
          choices = config.fieldPrettyChoices(field) || [];
        }
        return [
          {
            type: choices ? 'pretty-select' : 'pretty-text',
            shouldHideLabel: true,
            choices: choices ? [] : undefined,
            isLoading: config.fieldIsLoading(field),
            parentChoices: choices,
            parentKey: field.key,
            isSingleLine: field.zapierType !== 'text',
            isAccordion: choices ? false : true,
          },
        ];
      } else if (FieldUtils.isDictField(field)) {
        return [
          {
            type: 'pretty-text',
            shouldHideLabel: true,
            isAccordion: true,
          },
        ];
      }
      return origFieldItemFieldTemplates(field);
    },

    isValidRootValue({ fields, value }) {
      value = cleanObjectOfEmptyValues(value);
      return _.every(fields, field => {
        let fieldValue = value[field.key];
        if (
          shouldUseDefaults &&
          fieldValue === undefined &&
          field.default !== undefined
        ) {
          fieldValue = field.default;
        }
        if (config.fieldIsRequired(field) && fieldValue === undefined) {
          return false;
        }
        if (field.regex) {
          const re = new RegExp(field.regex);
          if (!re.test(fieldValue)) {
            return false;
          }
        }
        return true;
      });
    },

    fieldTemplateDefaultValue(fieldTemplate) {
      if (shouldUseDefaults) {
        if (fieldTemplate.type === 'dict') {
          return fieldTemplate.default || { '': '' };
        }
        if (fieldTemplate.type === 'list') {
          return fieldTemplate.default || [''];
        }
        return origFieldTemplateDefaultValue(fieldTemplate);
      }
      if (fieldTemplate.type === 'dict') {
        return { '': '' };
      }
      if (fieldTemplate.type === 'list') {
        return [''];
      }
      // This is a hack to fix https://github.com/zapier/zapier/issues/10455.
      // The real problem is that we're using PrettySelect for boolean fields
      // instead of PrettyBoolean. This is a mistake from the beginning:
      // http://zpr.io/nSXB4
      // Switching to PrettyBoolean would probably risk breaking something, so
      // instead, we just trick the field into thinking that our current value
      // matches the default value, which will make it hide the custom value
      // field.
      if (FieldUtils.isBoolField(fieldTemplate)) {
        // This is a field and not a field template.
        if (fieldTemplate.value) {
          const value = String(fieldTemplate.value).toLowerCase();
          if (value === 'yes' || value === 'no') {
            return value;
          }
        }
      }
      return undefined;
    },

    createElement_ConvertSearch: props => {
      return <ConvertSearchField {...props} />;
    },

    fieldLabel(field) {
      if (field.isWriteContainer) {
        const { node, service } = field.meta;
        const writeAction = ServiceUtils.getAssociatedAction({
          service,
          currentAction: node.action,
          currentTypeOf: node.type_of,
          desiredTypeOf: 'write',
        });
        const actionNoun = ServiceUtils.actionNoun(
          service,
          writeAction || '',
          'write'
        );
        const label = `Set up ${service.name} ${actionNoun}`;

        return label;
      }

      return origFieldLabel(field);
    },

    fieldCustomFieldTemplate(field) {
      const originalLabel = config.fieldLabel(field);
      const prefillParts = FieldUtils.splitPrefill(field.prefill);
      // superHumanize returns '' if passed undefined so no need to check targetKey here
      const name = superHumanize(prefillParts.targetKey);

      // Prevent quirky things like "ID ID".
      const customValueLabel =
        originalLabel === name ? originalLabel : `${originalLabel} ${name}`;

      let helpText =
        'Not sure how to use custom values? <a href="/help/create/customize/add-custom-values-to-dropdown-menu-fields-in-zaps" target="_blank">Learn more here</a>.';

      // This is weird but a little afraid to touch this logic:
      if (!prefillParts.targetKey || !prefillParts.displayKeys) {
        // do nothing
      } else if (prefillParts.displayKeys.length !== 1) {
        // do nothing
      } else if (prefillParts.targetKey === prefillParts.displayKeys[0]) {
        // do nothing
      } else {
        helpText = `Provide the ${customValueLabel}, not the ${superHumanize(
          prefillParts.displayKeys[0]
        )} here. ${helpText}`;
      }

      return {
        label: `Custom Value for ${customValueLabel}`,
        help_text: helpText,
        isCustomValue: true,
        isAccordion: true,
        required: config.fieldIsRequired(field),
      };
    },

    renderComponent_PrettyTag(component) {
      const { children, tag, field, onClick } = component.props;
      const { flow, node: currentNode } = field.meta;
      const tagNodeId = nodeIdFromTag(tag);
      // If no tagNodeId lookup from previous ancestors
      const inferredGive =
        !tagNodeId && FieldUtils.searchGivesFromTag(currentNode.id, flow, tag);
      const nodeId = inferredGive ? inferredGive.node_id : tagNodeId;
      const node = flow && flow.nodes && flow.nodes[nodeId];
      const api = node ? node.selected_api : null;
      const derivedChildren =
        (inferredGive && inferredGive.zap_meta_sample) || children;
      const isAutoMapped = NodeUtils.isAutomappedField(currentNode, field.key);

      const stepIndex =
        flow && node ? getStepIndex(flow.id, flow.nodes, node.id) : null;

      const tagNameIndex = tag.indexOf('__');
      const tagName = tagNameIndex >= 0 ? tag.substring(tagNameIndex + 2) : tag;
      const tagGive = FieldUtils.searchGivesFromTag(currentNode.id, flow, tag);

      const tagLabel = (tagGive && FieldUtils.giveLabel(tagGive)) || tagName;
      const tagValue = configurePreviewTag(derivedChildren, tag, field);

      return (
        <PreviewPill
          api={api}
          isAutoMapped={isAutoMapped}
          name={tagLabel}
          onClick={onClick}
          stepIndex={stepIndex}
          value={tagValue}
        />
      );
    },

    renderComponent_InsertButton(component) {
      const field = component.props.field;
      const isReadOnly = component.props.readOnly;
      const tabIndex =
        !FieldUtils.isPrettySelectField(field) &&
        !FieldUtils.isBoolField(field) &&
        !isReadOnly
          ? 0
          : null;
      const node = _.get(field, ['meta', 'node'], {});

      // path nodes don't have gives to insert
      if (isPathNode(node)) {
        return null;
      }

      return (
        <Div
          className={bemify('', 'insert-button', {
            'readonly-control': component.props.readOnly,
          })}
          onClick={component.props.onClick}
          onKeyDown={event => {
            if (event.keyCode === keycode('enter')) {
              component.props.onClick(event);
            }
          }}
          tabIndex={tabIndex}
          title="Insert a Field"
        />
      );
    },

    createElement_Label(props) {
      if (isListChild(props.field)) {
        return null;
      }
      return origCreateElement_Label(props);
    },

    createElement_Choice(props) {
      return <Choice {...props} />;
    },

    createElement_Filter(props) {
      return <Filter {...props} />;
    },

    createElement(name, props, children) {
      const field = get(props, ['field'], {});

      if (
        FieldUtils.isPrettySelectField(field) ||
        FieldUtils.isBoolField(field)
      ) {
        const newProps = {
          ...props,
          isAccordion: true,
          isAccordionAlwaysCollapsable: true,
          canOpenMultipleSections: true,
        };
        return origCreateElement(name, newProps, children);
      }

      return origCreateElement(name, props, children);
    },

    getChoicesMaxHeightRatio() {
      return 0.8;
    },
  };
};
